import type { SearchResultWithUIState } from './types';
import { StyledCell, StyledIcon } from './StyledComponents';
import React from 'react';
import { type FrequencyEnum, type ProxyTypeEnum, TAX_STATUS_LABELS } from 'venn-api';
import { Dates, getFormattedProxyType } from 'venn-utils';
import { GetColor } from 'venn-ui-kit';
import { cn } from '@/lib/utils';
import { NumericSpan, PercentageSpan } from 'venn-components';
import { getAnnRet, getAnnVol, getSharpe } from './utils';

export const emptyRenderer = () => {
  return <StyledCell className="empty-table-cell">--</StyledCell>;
};

export const iconsRender = (item: SearchResultWithUIState) => {
  if (!item.portfolioId && !item.fundId) {
    return '';
  }
  if (item.portfolioId) {
    return <StyledIcon type="folder" colorGetter={GetColor.DarkBlue} />;
  }

  if (item.investmentSource === 'CUSTOM') {
    return <StyledIcon type="layer-group" colorGetter={GetColor.DEPRECATED_DataLineColor.Pink} />;
  }

  return <StyledIcon type="square" colorGetter={GetColor.DEPRECATED_DataLineColor.Gold} />;
};
export const renderTimespan = (
  title: string,
  start?: number,
  end?: number,
  frequency?: FrequencyEnum,
  highlight?: boolean,
) => {
  if (start && end && frequency) {
    const startYear = Dates.toDDMMMYYYY(start, frequency);
    const endYear = Dates.toDDMMMYYYY(end, frequency);
    return (
      <div className="flex justify-between w-full gap-[1em]">
        {title}:
        <span className={cn('tooltip-value', highlight ? 'text-venn-gold' : 'text-venn-white')}>
          {startYear !== endYear ? `${startYear} - ${endYear}` : startYear}
        </span>
      </div>
    );
  }
  return null;
};

export const renderProxyType = (proxyType?: ProxyTypeEnum) => {
  if (proxyType) {
    return (
      <div className="flex justify-between w-full gap-[1em]">
        Proxy Type: <span className="tooltip-value">{getFormattedProxyType(proxyType)}</span>
      </div>
    );
  }
  return null;
};

export const renderExtrapolation = (extrapolated: boolean) => {
  return extrapolated ? (
    <div className="flex justify-between w-full gap-[1em]">
      Extrapolation: <span className="tooltip-value">On</span>
    </div>
  ) : null;
};

export const renderTotalReturn = (item: SearchResultWithUIState) => {
  const periodReturn = getAnnRet(item);
  if (periodReturn === 0 || periodReturn) {
    return (
      <div className="flex justify-between w-full gap-[1em]">
        Annualized Return*:&nbsp;
        <PercentageSpan className="tooltip-value" value={periodReturn} />
      </div>
    );
  }
  return null;
};

export const renderVolatility = (item: SearchResultWithUIState) => {
  const vol = getAnnVol(item);
  if (vol === 0 || vol) {
    return (
      <div className="flex justify-between w-full gap-[1em]">
        Volatility*:&nbsp;
        <PercentageSpan className="tooltip-value" value={vol} />
      </div>
    );
  }
  return null;
};

export const renderSharpeRatio = (item: SearchResultWithUIState) => {
  const sharpe = getSharpe(item);
  if (sharpe === 0 || sharpe) {
    return (
      <div className="flex justify-between w-full gap-[1em]">
        Sharpe*:&nbsp;
        <NumericSpan className="tooltip-value" precision={2}>
          {sharpe}
        </NumericSpan>
      </div>
    );
  }
  return null;
};

export const renderTaxStatus = (item: SearchResultWithUIState) => {
  const { taxStatuses } = item;
  if (taxStatuses && taxStatuses?.length > 0) {
    const displayLabels = taxStatuses.map((status) => TAX_STATUS_LABELS[status]);
    return (
      <div className="flex justify-between w-full gap-[1em]">
        Tax Status:&nbsp;
        <span className="tooltip-value">{displayLabels.join(', ')}</span>
      </div>
    );
  }
  return null;
};
